import { RequestManager } from './request_manager';
import { EmailToken, EmailTokenParameters } from './tokens/email';
import { ThirdPartyToken } from './tokens/third_party';
import { Token, TokenClass } from './tokens/token';
import { YouVersionToken, YouVersionTokenParameters } from './tokens/youversion';
import { IUser, User, UserHeader } from './user';

export { Token, TokenClass } from './tokens/token';
export type { IUser } from './user';

export interface ThirdPartyTokenProvider {
  getToken(): string;
  getTokenClass(): TokenClass;
  getUserId(): string;
}

export class Authentication {
  private readonly requestManager: RequestManager;

  constructor(
    public readonly clientId: string,
    public readonly clientSecret: string,
    staging = false
  ) {
    this.requestManager = new RequestManager();
    this.requestManager.staging = staging;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async loginFromStorageItem(item: any, requireRefresh = true, canRefreshUser = false): Promise<Token> {
    let token: Token;
    switch (item.type) {
      case TokenClass.email:
      case TokenClass.scopedEmail:
        token = EmailTokenParameters.fromStorageItem(item);
        break;
      case TokenClass.youversion:
        token = YouVersionTokenParameters.fromStorageItem(this.clientId, this.clientSecret, this.requestManager, item);
        break;
      default:
        throw new Error(`Unsupported storage item: ${item.type}`);
    }
    if (token.willExpireSoon || token.expired) {
      try {
        token = await token.refresh();
      } catch (e) {
        if (requireRefresh) {
          throw e;
        }
      }
    }
    if (canRefreshUser && !token.expired) {
      try {
        token.user = await this.getUser(token);
      } catch (e) {
        // silently fail
      }
    }
    return token;
  }

  async getUser(token: Token): Promise<IUser> {
    if (token.expired) {
      token = await token.refresh();
    }
    const response = await this.requestManager.getApi('users', '/view.json', '3.1', token);
    token.user = new User(response.response.data);
    return token.user;
  }

  private _newYouVersionToken(user: IUser): YouVersionToken {
    return new YouVersionToken(
      this.clientId,
      this.clientSecret,
      this.requestManager,
      user,
      new YouVersionTokenParameters({})
    );
  }

  async loginWithEmail(
    email: string,
    password: string,
    scopes: Array<string>,
    onRefreshed: (token: Token) => void
  ): Promise<Token> {
    const emailToken = new EmailToken(new UserHeader(0, email, email), new EmailTokenParameters(password, scopes));
    const user = await this.getUser(emailToken);
    emailToken.user = user;
    let yvToken: Token = this._newYouVersionToken(user);
    yvToken.initialize(emailToken, onRefreshed);
    yvToken.user = user;
    yvToken = await yvToken.refresh();
    return yvToken;
  }

  private async _loginWithThirdParty(token: string, tokenClass: TokenClass): Promise<IUser> {
    const response = await this.requestManager.postApi(
      'users',
      '/authenticate.json',
      '3.1',
      new ThirdPartyToken(token, tokenClass)
    );
    return new User(response.response.data);
  }

  async loginWithThirdParty(provider: ThirdPartyTokenProvider, onRefreshed: (token: Token) => void): Promise<Token> {
    const user = await this._loginWithThirdParty(provider.getToken(), provider.getTokenClass());
    let yvToken: Token = this._newYouVersionToken(user);
    yvToken.initialize(new ThirdPartyToken(provider.getToken(), provider.getTokenClass()), onRefreshed);
    yvToken = await yvToken.refresh();
    yvToken.user = await this.getUser(yvToken);
    return yvToken;
  }
}
